import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Row, RowState } from "./Row";
import { Clue, clue, CluedLetter } from "./clue";
import { Keyboard } from "./Keyboard";
import { maxGuesses, day1Date } from "./util";
import { makePuzzle } from "./Game";
import { longPollGameState, RemoteGameState, fetchGameStateForPuzzle } from "./gameStateApi";

interface WatchViewProps {
  puzzleId: string;
  colorBlind: boolean;
  keyboardLayout: string;
  targetUserId?: string;
  watchLabel?: string;
  onStatusChange?: (status: "in_progress" | "won" | "lost") => void;
  children?: ReactNode;
}

export function WatchView({ puzzleId, colorBlind, keyboardLayout, targetUserId, watchLabel, onStatusChange, children }: WatchViewProps) {
  const [remote, setRemote] = useState<RemoteGameState | null>(null);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const puzzleNum = parseInt(puzzleId.replace("practice-", ""), 10);
  const isPractice = puzzleId.startsWith("practice-");

  const puzzle = !isNaN(puzzleNum) ? makePuzzle(puzzleNum) : null;

  useEffect(() => {
    let active = true;

    async function run() {
      if (targetUserId) {
        const data = await longPollGameState(targetUserId, puzzleId, null);
        if (!active) return;
        if (data) {
          setRemote(data);
          setError(false);
          await longPollLoop(targetUserId, data.updated_at || null);
        } else {
          setError(true);
        }
        return;
      }

      const initial = await fetchGameStateForPuzzle(puzzleId);
      if (!active) return;
      if (!initial || !initial.user_id) {
        setError(true);
        while (active) {
          await new Promise((r) => setTimeout(r, 3000));
          if (!active) return;
          const retry = await fetchGameStateForPuzzle(puzzleId);
          if (!active) return;
          if (retry && retry.user_id) {
            setRemote(retry);
            setError(false);
            await longPollLoop(retry.user_id, retry.updated_at || null);
            return;
          }
        }
        return;
      }

      setRemote(initial);
      setError(false);
      await longPollLoop(initial.user_id, initial.updated_at || null);
    }

    async function longPollLoop(userId: string, after: string | null) {
      while (active) {
        const controller = new AbortController();
        abortRef.current = controller;
        const data = await longPollGameState(userId, puzzleId, after, controller.signal);
        if (!active) return;
        if (data) {
          setRemote(data);
          after = data.updated_at || null;
        }
      }
    }

    run();

    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [puzzleId, targetUserId]);

  const dateOnlyText = !isPractice && !isNaN(puzzleNum)
    ? new Date(day1Date.getTime() + (puzzleNum - 1) * 86400 * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : "";

  const dayHeader = (
    <div className="Game-options">
      {!isPractice && <span className="DayNum">Day {puzzleNum}{dateOnlyText ? `, ${dateOnlyText}` : ""}</span>}
      {isPractice && <span>Unlimited</span>}
    </div>
  );

  useEffect(() => {
    if (onStatusChange && remote) {
      onStatusChange(remote.metadata.status);
    }
  }, [remote?.metadata.status, onStatusChange, remote]);

  if (error && !remote) {
    return (
      <div className="Game">
        {dayHeader}
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Waiting for player to start...
        </div>
      </div>
    );
  }

  if (!remote) {
    return (
      <div className="Game">
        {dayHeader}
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Loading...
        </div>
      </div>
    );
  }

  const gameOver = remote.metadata.status === "won" || remote.metadata.status === "lost";
  const isLive = remote.metadata.status === "in_progress";
  const rowWidth = puzzle ? puzzle.target.length : 7;

  const letterInfo = new Map<string, [Clue, number]>();

  const allGuesses = remote.state.guesses;
  const currentGuessText = remote.state.currentGuess || "       ";
  const cursorPosition = remote.state.cursorPosition ?? 0;

  const cluedRows: CluedLetter[][] = puzzle
    ? allGuesses.map((g) => clue(g.word, puzzle.target))
    : allGuesses.map((g) => g.clues.map((s) => ({ letter: s.letter, clue: s.clue as Clue })));

  for (const row of cluedRows) {
    for (const { clue: c, letter } of row) {
      if (c === undefined) break;
      const count = row
        .filter((cl) => cl.letter === letter && cl.clue && cl.clue > Clue.Absent)
        .length;
      const old = letterInfo.get(letter);
      const newCount = old === undefined || old[1] < count ? count : old[1];
      const newClue = old === undefined || old[0] < c ? c : old[0];
      letterInfo.set(letter, [newClue, newCount]);
    }
  }

  const totalRows = remote.metadata.status === "won"
    ? allGuesses.length
    : maxGuesses;

  const tableRows = Array(totalRows)
    .fill(undefined)
    .map((_, i) => {
      if (i < allGuesses.length) {
        return (
          <Row
            key={i}
            rowState={RowState.LockedIn}
            cluedLetters={cluedRows[i]}
            cluedRows={cluedRows.slice(0, i)}
            letterInfo={letterInfo}
            currentGuess={["       ", 0]}
            rowNumber={i}
            annotation={"\u00A0"}
            rowWidth={rowWidth}
            onClick={() => {}}
          />
        );
      }

      if (i === allGuesses.length && isLive) {
        const typingClues: CluedLetter[] = [];
        for (let c = 0; c < rowWidth; c++) {
          typingClues.push(
            c < currentGuessText.length
              ? { letter: currentGuessText[c], clue: Clue.Absent }
              : { letter: " ", clue: undefined }
          );
        }
        return (
          <Row
            key={i}
            rowState={RowState.Editing}
            cluedLetters={typingClues}
            cluedRows={cluedRows}
            letterInfo={letterInfo}
            currentGuess={[currentGuessText, cursorPosition]}
            rowNumber={i}
            annotation={"\u00A0"}
            rowWidth={rowWidth}
            onClick={() => {}}
          />
        );
      }

      const emptyClues: CluedLetter[] = Array(rowWidth)
        .fill(undefined)
        .map(() => ({ letter: " ", clue: undefined as Clue | undefined }));
      return (
        <Row
          key={i}
          rowState={RowState.Pending}
          cluedLetters={emptyClues}
          cluedRows={cluedRows}
          letterInfo={letterInfo}
          currentGuess={["       ", 0]}
          rowNumber={i}
          annotation={"\u00A0"}
          rowWidth={rowWidth}
          onClick={() => {}}
        />
      );
    });

  return (
    <div className="Game">
      <div className="watch-banner-wrapper">
        <div className={`watch-banner${isLive ? " watch-banner--live" : ""}`}>
          {isLive && <span className="watch-dot" />}
          {watchLabel ? watchLabel : isLive ? "Watching Live" : gameOver ? (remote.metadata.status === "won" ? "Replay \u2014 Player Won" : "Replay \u2014 Player Lost") : "Watch Mode"}
        </div>
      </div>
      <div className={`watch-border${isLive ? " watch-border--live" : ""}`}>
      <table
        className="Game-rows"
        tabIndex={0}
        aria-label="watched game board"
      >
        <tbody>{tableRows}</tbody>
      </table>
      </div>
      {children}

      {gameOver && puzzle && (<p>
        The word was {puzzle.target.toUpperCase().replaceAll(" ", "\u23B5")}.</p>)}

      <Keyboard
        layout={keyboardLayout}
        letterInfo={letterInfo}
        onKey={() => {}}
      />
    </div>
  );
}
